import React, {useCallback, useMemo, useState} from 'react';

import isEqual from 'lodash/isEqual';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';

import {createStyles, makeStyles, Theme} from '@components/@theme';

import Autocomplete, {AutocompleteProps} from '@components/Autocomplete';
import {useDebounce} from '@hooks/useDebounce';

const DEF_GET_OPTION_LABEL = option => option.title;
const DEF_GET_OPTION_VALUE = option => option.value;
const DEF_VALUE_TO_OPTION = (value, title?: string) =>
  ({
    title: title || value,
    value,
  } as any);
const DEF_OPTIONS = [];

export interface AutoCompleteBaseProps<
  T = {title: string; value: number | string},
  Multiple = undefined
> extends Pick<
    // TODO: remove ts-ignore and fix the problem with Multiple type. It works correctly, but shows an error:
    //  Type 'Multiple' does not satisfy the constraint 'boolean | undefined'.
    //  Type 'Multiple' is not assignable to type 'true'.
    // @ts-ignore
    AutocompleteProps<T, Multiple>,
    'multiple' | 'value' | 'groupBy' | 'style' | 'renderOption' | 'getOptionDisabled'
  > {
  label?: string;
  placeholder?: string;
  options?: Array<T>;
  getLabel?: (option: T) => string;
  getValue?: (option: T) => any | undefined;
  onChange: (newValue: any) => void;
  className?: string;
  valueToOption?: (inputText: string, label?: string) => T;
  onCreate?: (option: Partial<T>) => void | null;
  onLoad?: (searchTerm: string) => void;
  fullWidth?: boolean;
  loading?: boolean;
  disableCloseOnSelect?: boolean;
  autoHighlight?: boolean;
  onBlur?: any;
  noOptionsText?: string;
  showOptionDividers?: boolean;
  readOnly?: boolean;
}

export const AutoCompleteBase = <T, Multiple extends boolean | undefined>({
  className,
  valueToOption = DEF_VALUE_TO_OPTION,
  label,
  onChange,
  options = DEF_OPTIONS,
  placeholder,
  getLabel = DEF_GET_OPTION_LABEL,
  getValue = DEF_GET_OPTION_VALUE,
  onLoad,
  onCreate,
  loading,
  showOptionDividers = false,
  renderOption,
  value,
  readOnly = false,
  ...props
}: AutoCompleteBaseProps<T, Multiple>) => {
  const [optionForCreate, setOptionForCreate] = useState<T | null>();

  const asyncHandleInputChange = useCallback(
    event => {
      const inputText = event.target.value;
      if (inputText?.length > 2) {
        if (onCreate) {
          setOptionForCreate(valueToOption(inputText, `Add "${inputText}"`));
        }
      } else {
        setOptionForCreate(null);
      }

      if (onLoad) {
        onLoad(inputText);
      }
    },
    [setOptionForCreate, valueToOption, onCreate, onLoad]
  );

  const handleInputChange = useDebounce(asyncHandleInputChange);

  const {circularProgress, focused, input, inputRoot, option, popper, root, tagSizeSmall} =
    useStyles({showOptionDividers});
  const renderInput = useCallback(
    props => {
      const renderStart = arr => {
        if (!Array.isArray(arr) || !renderOption) {
          return arr;
        }
        return arr.map((el, index) => {
          return React.cloneElement(el, {
            label: (renderOption as any)(value?.[index]),
          });
        });
      };

      const preparedProps = {
        ...props,
        InputProps: {
          ...props.InputProps,
          startAdornment: renderOption
            ? renderStart(props.InputProps.startAdornment)
            : props.InputProps.startAdornment,
          endAdornment: (
            <React.Fragment>
              {loading ? (
                <CircularProgress className={circularProgress} color="primary" size={18} />
              ) : null}
              {props.InputProps.endAdornment}
            </React.Fragment>
          ),
          readOnly: readOnly,
        },
        inputProps: {
          ...props.inputProps,
          onChange: (...args) => {
            args[0].persist();
            handleInputChange(...args);
            props.inputProps.onChange(...args);
          },
        },
      };
      return (
        <TextField {...preparedProps} variant="outlined" label={label} placeholder={placeholder} />
      );
    },
    [
      circularProgress,
      handleInputChange,
      label,
      loading,
      placeholder,
      renderOption,
      value,
      readOnly,
    ]
  );

  const preparedOptions = useMemo<T[]>(() => {
    if (optionForCreate) {
      return [optionForCreate, ...options] as T[];
    }

    return options;
  }, [optionForCreate, options]);

  const handleChange = useCallback(
    (_event, newValue) => {
      if (onCreate && optionForCreate) {
        onCreate(optionForCreate);
      }
      setOptionForCreate(null);
      if (Array.isArray(newValue)) {
        onChange(newValue.map(getValue));
      } else {
        onChange(getValue(newValue));
      }
    },
    [getValue, optionForCreate, onCreate, onChange, setOptionForCreate]
  );

  return (
    <Autocomplete<T, Multiple>
      filterSelectedOptions={props.multiple}
      clearOnBlur={false}
      size="small"
      {...props}
      getOptionSelected={isEqual}
      loading={loading}
      className={className}
      onChange={handleChange}
      classes={{focused, input, inputRoot, option, popper, root, tagSizeSmall}}
      options={preparedOptions}
      getOptionLabel={getLabel}
      renderInput={renderInput}
      renderOption={renderOption}
      value={value}
    />
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    circularProgress: {
      position: 'absolute',
      right: 36,
    },
    focused: {
      '&:hover > div > div > fieldset': {
        borderColor: '#b3b3b3!important',
      },
      '& > div > div > fieldset': {
        borderColor: '#a9a9a9!important',
        borderWidth: '1px!important',
        boxShadow: 'inset 0 1px 4px rgb(0 0 0 / 20%)!important',
      },
    },
    inputRoot: {
      padding: '3.21px 54px 3.21px 3.21px!important',
    },
    input: {},
    option: {
      height: 32,
      minHeight: 32,
      paddingLeft: 10,
    },
    popper: {
      zIndex: theme.elevation.aboveModalWindow,
      '& > div': {
        boxShadow: '3px 3px 12px rgba(0, 0, 0, 0.2)',
        padding: '0 5px 0 10px',
        '& li': {
          marginRight: 16,
          '&:not(:last-child)': {
            borderBottom: (props: {showOptionDividers: boolean}) =>
              `${props.showOptionDividers ? '1' : '0'}px solid ${theme.palette.divider}`,
          },
        },
      },
    },
    root: {
      // This media queries required to support consistency with old styles
      '@media screen and (min-width: 1424px)': {
        maxWidth: 400,
        minWidth: 400,
        width: 400,
      },
      '@media screen and (max-width: 1423px) and (min-width: 1024px)': {
        maxWidth: 285,
        minWidth: 285,
        width: 285,
      },
      '@media screen and (max-width: 1023px)': {
        maxWidth: 200,
        minWidth: 200,
        width: 200,
      },
      '&:hover > div > div > fieldset': {
        borderColor: '#b3b3b3!important',
      },
    },
    tagSizeSmall: {
      margin: 2,
    },
  })
);
